import React, { useEffect, useMemo, useRef, useState } from "react";
import HierarchySceneCanvas from "../../../components/definition-editor/HierarchySceneCanvas";
import {
  buildSceneViewSettingsFromDrawingView,
  getViewportStyle,
} from "../../engine/viewportUtils";
import DrawingDimensionLayer from "../annotations/DrawingDimensionLayer";
import ViewLabel from "../views/ViewLabel";
import {
  getViewportLayoutMm,
  normalizeViewportToMm,
} from "../../engine/sheetLayoutUtils";
import SectionMarker from "../views/SectionMarker";
import DimensionHoverPopover from "../../../components/dimensions/DimensionHoverPopover";
import { unprojectViewportMmToWorld } from "../../engine/viewProjection";
import DrawingReferenceLayer from "../annotations/DrawingReferenceLayer";

const VIEW_MODE_OPTIONS = [
  { value: "hiddenLine", label: "Hidden Line" },
  { value: "wireframe", label: "Wireframe" },
  { value: "realistic", label: "Realistic" },
  { value: "xray", label: "X-Ray" },
  { value: "full", label: "Full" },
  { value: "boards", label: "Boards" },
  { value: "box", label: "Box" },
];

const SCALE_OPTIONS = [5, 10, 20, 25, 50, 75, 100];

export default function SheetViewport({
  viewport,
  view,
  sheet,
  pxPerMm,
  sheetZoom = 1,
  sectionMarkers = [],
  drawingTool,
  onCreateSectionFromView,
  dimensionDraft,
  onDimensionPick,
  selected = false,
  isActive,
  onSelect,
  onMoveViewport,
  onResizeViewport,
  onUpdateViewport,
  onUpdateView,
  onActivate,
  rootInstances,
  definitions,
  globalParameters,
  room,
  selectedRoomObjectId,
  onSelectRoomObject,
  selectedId,
  selectedIds,
  onMultiSelect,
  onSelectNode,
  onResolvedSelect,
  onDoubleOpen,
  onTransformNode,
  onChangeNode,
  materials,
  selectedAnnotationId,
onSelectAnnotation,
onUpdateAnnotation,
}) {
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const paperPxPerMm = Number(pxPerMm || 1);
const sheetScreenPxPerMm = paperPxPerMm;
const viewportRenderDpr = Math.min(
  4,
  Math.max(
    1,
    (typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1) *
      Math.max(1, Number(sheetZoom || 1))
  )
);

  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [dimensionHover, setDimensionHover] = useState(null);
  const [layoutPreview, setLayoutPreview] = useState(null);

  const uiFont = Math.max(10, 2.7 * pxPerMm);
const uiPadY = Math.max(2, 0.9 * pxPerMm);
const uiPadX = Math.max(4, 1.5 * pxPerMm);
const uiRadius = Math.max(4, 1.2 * pxPerMm);

const visualViewport = layoutPreview || viewport;

const sceneViewSettings = useMemo(() => {
  return buildSceneViewSettingsFromDrawingView(view, false, viewport);
}, [view, viewport]);

  const viewportAnnotations = useMemo(() => {
    return (sheet.annotations || []).filter(
      (ann) => ann.viewportId === viewport.id
    );
  }, [sheet.annotations, viewport.id]);

const viewportWidthPx =
  Number(viewport?.widthMm ?? viewport?.width ?? 100) * sheetScreenPxPerMm;

const viewportHeightPx =
  Number(viewport?.heightMm ?? viewport?.height ?? 80) * sheetScreenPxPerMm;

  function startDrag(e) {
  if (isActive) return;

  e.preventDefault();
  e.stopPropagation();

  onSelect?.();

  const layout = getViewportLayoutMm(viewport);
  const normalized = normalizeViewportToMm(viewport);

  dragRef.current = {
    startClientX: e.clientX,
    startClientY: e.clientY,
    startXmm: layout.xMm,
    startYmm: layout.yMm,
    normalized,
  };

  setDragging(true);

  window.addEventListener("mousemove", handleDragMove);
  window.addEventListener("mouseup", stopDrag);
}

useEffect(() => {
  if (!isActive || drawingTool !== "dimension") {
    setDimensionHover(null);
  }
}, [isActive, drawingTool]);

function getWorldPointFromViewportClick(e) {
  const rect = e.currentTarget.getBoundingClientRect();

  const pxX = e.clientX - rect.left;
  const pxY = e.clientY - rect.top;

const layout = getViewportLayoutMm(viewport);

const pointMm = {
  x: (pxX / rect.width) * layout.widthMm,
  y: (pxY / rect.height) * layout.heightMm,
};

  return unprojectViewportMmToWorld(pointMm, view, viewport);
}

function handleDragMove(e) {
  const drag = dragRef.current;
  if (!drag) return;

  const dxMm = (e.clientX - drag.startClientX) / sheetScreenPxPerMm;
const dyMm = (e.clientY - drag.startClientY) / sheetScreenPxPerMm;

  const nextXmm = drag.startXmm + dxMm;
  const nextYmm = drag.startYmm + dyMm;

  const nextViewport = {
    ...drag.normalized,
    xMm: nextXmm,
    yMm: nextYmm,
  };

  dragRef.current.nextViewport = nextViewport;
  setLayoutPreview(nextViewport);
}

function stopDrag() {
  const drag = dragRef.current;

  if (drag?.nextViewport) {
    onUpdateViewport?.(sheet.id, viewport.id, drag.nextViewport);
  }

  dragRef.current = null;
  setLayoutPreview(null);
  setDragging(false);

  window.removeEventListener("mousemove", handleDragMove);
  window.removeEventListener("mouseup", stopDrag);
}

function startResize(e) {
  if (isActive) return;

  e.preventDefault();
  e.stopPropagation();

  onSelect?.();

  const layout = getViewportLayoutMm(viewport);
  const normalized = normalizeViewportToMm(viewport);

  resizeRef.current = {
    startClientX: e.clientX,
    startClientY: e.clientY,
    startWidthMm: layout.widthMm,
    startHeightMm: layout.heightMm,
    normalized,
  };

  setResizing(true);

  window.addEventListener("mousemove", handleResizeMove);
  window.addEventListener("mouseup", stopResize);
}

function handleResizeMove(e) {
  const resize = resizeRef.current;
  if (!resize) return;

  const dxMm = (e.clientX - resize.startClientX) / sheetScreenPxPerMm;
const dyMm = (e.clientY - resize.startClientY) / sheetScreenPxPerMm;

  const nextWidthMm = Math.max(30, resize.startWidthMm + dxMm);
  const nextHeightMm = Math.max(20, resize.startHeightMm + dyMm);

  const nextViewport = {
    ...resize.normalized,
    widthMm: nextWidthMm,
    heightMm: nextHeightMm,
  };

  resizeRef.current.nextViewport = nextViewport;
  setLayoutPreview(nextViewport);
}

function stopResize() {
  const resize = resizeRef.current;

  if (resize?.nextViewport) {
    onUpdateViewport?.(sheet.id, viewport.id, resize.nextViewport);
  }

  resizeRef.current = null;
  setLayoutPreview(null);
  setResizing(false);

  window.removeEventListener("mousemove", handleResizeMove);
  window.removeEventListener("mouseup", stopResize);
}

const is3DView =
  view?.type === "iso" ||
  view?.cameraType === "perspective";

  const canvasInteractive =
  isActive && (is3DView || drawingTool === "dimension");

  return (
    <div
      style={getViewportStyle(
  visualViewport,
  isActive,
  selected,
  dragging || resizing,
  pxPerMm
)}
      onMouseDown={(e) => {
        e.stopPropagation();
        onSelect?.();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onActivate?.(viewport.id);
      }}
    >
      <ViewLabel
  title={viewport.title || view?.name || "View"}
  scale={view?.scale}
  isActive={isActive}
  pxPerMm={pxPerMm}
/>

      {selected || isActive ? (
  <div
    style={{
  position: "absolute",
  right: 2 * pxPerMm,
  top: 2 * pxPerMm,
  zIndex: 90,
  background: "rgba(255,255,255,0.95)",
  border: "1px solid #cbd5e1",
  borderRadius: uiRadius,
  padding: `${uiPadY}px ${uiPadX}px`,
  boxShadow: "0 2px 8px rgba(15,23,42,0.12)",
  display: "flex",
  gap: 1.5 * pxPerMm,
  alignItems: "center",
}}
    onMouseDown={(e) => e.stopPropagation()}
    onDoubleClick={(e) => e.stopPropagation()}
  >
    <select
  value={view?.viewMode || "hiddenLine"}
  onChange={(e) => {
    if (!view?.id) return;

    onUpdateView?.(view.id, {
      viewMode: e.target.value,
    });
  }}
  style={{
  fontSize: uiFont,
  border: "none",
  outline: "none",
  background: "transparent",
  color: "#334155",
  fontWeight: 600,
}}
>
  {VIEW_MODE_OPTIONS.map((option) => (
    <option key={option.value} value={option.value}>
      {option.label}
    </option>
  ))}
</select>

<select
  value={Number(view?.scale || 20)}
  onChange={(e) => {
  if (!view?.id) return;

  const nextScale = Number(e.target.value);
  const is3D =
    view?.type === "iso" ||
    view?.cameraType === "perspective";

  const isSection = view?.type === "section";

onUpdateView?.(view.id, (oldView) => ({
  scale: nextScale,
  projection: oldView.projection || {
    originWorld: { x: 0, y: 0, z: 0 },
  },
  camera: null,
}));
}}
  style={{
  fontSize: uiFont,
  border: "none",
  outline: "none",
  background: "transparent",
  color: "#334155",
  fontWeight: 600,
}}
>
  {SCALE_OPTIONS.map((scale) => (
    <option key={scale} value={scale}>
      1:{scale}
    </option>
  ))}
</select>
  </div>
) : null}
      

      <div
  style={{
    position: "absolute",
    inset: 0,
    pointerEvents: canvasInteractive ? "auto" : "none",
  }}
onWheelCapture={(e) => {
  if (!canvasInteractive) {
    e.stopPropagation();
  }
}}
>
  <HierarchySceneCanvas
    rootInstances={rootInstances}
    definitions={definitions}
    globalParameters={globalParameters}
    room={room}
    selectedRoomObjectId={selectedRoomObjectId}
    onSelectRoomObject={isActive ? onSelectRoomObject : undefined}
    selectedId={isActive && is3DView ? selectedId : null}
    selectedIds={isActive && is3DView ? selectedIds : []}
    onMultiSelect={isActive && is3DView ? onMultiSelect : undefined}
onSelect={isActive && is3DView ? onSelectNode : undefined}
onResolvedSelect={isActive && is3DView ? onResolvedSelect : undefined}
onDoubleOpen={isActive && is3DView ? onDoubleOpen : undefined}
onTransformNode={isActive && is3DView ? onTransformNode : undefined}
onChangeNode={isActive && is3DView ? onChangeNode : undefined}
    toolMode={
  canvasInteractive && drawingTool === "dimension"
    ? "dimension"
    : canvasInteractive && is3DView
      ? "select"
      : "view"
}
    setToolMode={() => {}}
    viewMode={view?.viewMode || "hiddenLine"}
    materials={materials}
    showMountingDrillings={false}
    showCoordinateSystems={false}
    sceneViewSettings={sceneViewSettings}
    viewportMode
    viewportActive={canvasInteractive}
    lockedCamera={!canvasInteractive}
    renderDpr={viewportRenderDpr}
    dimensionDraft={dimensionDraft}
    onDimensionPick={(payload) => {
      onDimensionPick?.({
        ...payload,
        viewport,
        view,
      });
    }}
    onDimensionHover={setDimensionHover}
    onCameraChange={(cameraState) => {
      if (!view?.id) return;

      const is3D =
        view?.type === "iso" ||
        view?.cameraType === "perspective";

      if (!is3D) return;

      onUpdateView?.(view.id, {
        camera: cameraState,
      });
    }}
  />
</div>

<DrawingReferenceLayer
  hover={dimensionHover}
  viewport={viewport}
  view={view}
  active={isActive && drawingTool === "dimension"}
  screenPxPerMm={sheetScreenPxPerMm}
/>

      <DrawingDimensionLayer
  annotations={viewportAnnotations}
  viewport={viewport}
  view={view}
  active={isActive && drawingTool !== "dimension"}
  selectedAnnotationId={selectedAnnotationId}
  onSelectAnnotation={onSelectAnnotation}
  onUpdateAnnotation={onUpdateAnnotation}
  screenPxPerMm={sheetScreenPxPerMm}
/>

      {drawingTool === "dimension" && isActive ? (
  <DimensionHoverPopover
  hover={dimensionHover}
  viewportWidthPx={viewportWidthPx}
  viewportHeightPx={viewportHeightPx}
  pxPerMm={pxPerMm}
/>
) : null}

     {drawingTool === "dimension" &&
dimensionDraft?.viewportId === viewport.id &&
dimensionDraft?.ref1 ? (
  <div
    style={{
  position: "absolute",
  left: 2 * pxPerMm,
  bottom: 2 * pxPerMm,
  zIndex: 120,
  background: "rgba(37,99,235,0.95)",
  color: "white",
  borderRadius: 1.5 * pxPerMm,
  padding: `${1 * pxPerMm}px ${2 * pxPerMm}px`,
  fontSize: 2.7 * pxPerMm,
  fontWeight: 600,
  pointerEvents: "none",
}}
  >
    First reference selected · pick second face
  </div>
) : null}

     {drawingTool !== "dimension" &&
  (sectionMarkers || []).map((marker) => (
    <SectionMarker
      key={marker.markerId || marker.label}
      marker={marker}
      viewport={viewport}
      screenPxPerMm={sheetScreenPxPerMm}
      view={view}
      pxPerMm={pxPerMm}
      selected={String(selectedAnnotationId) === String(marker.markerId)}
      onSelect={() => onSelectAnnotation?.(marker.markerId)}
      onUpdate={(patch) => {
        if (!marker.viewId) return;

        onUpdateView?.(marker.viewId, (sectionView) => ({
          sectionPlane: {
            ...(sectionView.sectionPlane || {}),
            ...patch,
          },
          camera: null,
        }));
      }}
    />
  ))}

      {!isActive ? (
  <div
    className={dragging ? "cursor-grabbing" : "cursor-move"}
    style={{
      position: "absolute",
      inset: 0,
      zIndex: 40,
      background: "transparent",
      cursor: dragging ? "grabbing" : "move",
    }}
    onMouseDown={startDrag}
    onClick={(e) => {
  e.stopPropagation();
  onSelect?.();

  if (drawingTool === "section" && view?.type === "top") {
    const worldPoint = getWorldPointFromViewportClick(e);

    onCreateSectionFromView?.({
      sourceView: view,
      sourceViewport: viewport,
      worldPoint,
    });
  }
}}
    title="Drag viewport"
  />
) : null}

      {selected && !isActive ? (
  <div
    style={{
      position: "absolute",
      right: 0,
      bottom: 0,
      width: Math.max(12, 5 * Number(pxPerMm || 1)),
      height: Math.max(12, 5 * Number(pxPerMm || 1)),
      zIndex: 100,
      background: "#0ea5e9",
      border: "2px solid white",
      boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
      cursor: "se-resize",
    }}
    onMouseDown={startResize}
    title="Resize viewport"
  />
) : null}
    </div>
  );
}